/*
  Pure helpers for the project detail page (UI overhaul, wave 1). No React, no API — unit-tested in
  src/__tests__/project-detail-model.test.js.

  Stages:     isStageDone, buildStagePipeline, waitingPartyOf, collectWaitingOn, canSeeLpoLoiStage
  Execution:  summarizeExecution, getChildProgress, describeExecutionCascade, isIncompleteDate, EXECUTION_COLUMNS
  Material:   getMaterialProgress, createMaterialMatcher, findUnlinkedMaterial, formatCodeList
  Structure:  summarizeStructure, categorizeDevice, countStructureFloors
  Invoicing:  buildMoneyBar, countInvoicesByScope
  Variations: summarizeVariations, variationItemsPreview, parseMoneyInput
  Timeline:   getHandoverState
  Files:      splitFolderPath
*/
import { ageDays, toNumber } from '@/lib/format';
import { hasPermission } from '@/lib/permissions';

const num = (value) => {
  const parsed = toNumber(value);
  return parsed === null ? 0 : parsed;
};
const text = (value) => String(value ?? '').trim();
const naturalCompare = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });

/* ------------------------------------------------------------------ stages */

export const isStageDone = (status) => status === 'Approved' || status === 'Completed';

/** Display status of a stage ('Completed' is shown as 'Approved', like the stage status picker). */
export const stageDisplayStatus = (status) => (status === 'Completed' ? 'Approved' : (text(status) || 'Not Started'));

export const stageNumber = (stage, index = 0) => String(Number(stage?.order) || index + 1).padStart(2, '0');

/**
 * Ordered pipeline steps. The first stage that is not approved is the "current" (next) one.
 * → [{ id, name, number, status, state: 'done' | 'current' | 'todo' }]
 */
export function buildStagePipeline(stages = []) {
  const ordered = [...(stages || [])].sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));
  const currentIndex = ordered.findIndex((stage) => !isStageDone(stage?.status));
  return ordered.map((stage, index) => ({
    id: stage.id,
    name: text(stage.name) || `Stage ${index + 1}`,
    number: stageNumber(stage, index),
    status: stageDisplayStatus(stage.status),
    state: isStageDone(stage.status) ? 'done' : (index === currentIndex ? 'current' : 'todo'),
  }));
}

export const WAITING_PARTIES = [
  { key: 'sales', label: 'Sales' },
  { key: 'presales', label: 'Presales' },
  { key: 'contractor', label: 'Contractor' },
  { key: 'execution', label: 'Execution team' },
];

/** 'Pending from sales' → 'sales'; anything that is not "Pending from …" → null. */
export function waitingPartyOf(status) {
  const match = text(status).match(/^pending\s+from\s+(.+)$/i);
  if (!match) return null;
  const who = match[1].trim().toLowerCase();
  if (who.startsWith('presale')) return 'presales';
  if (who.startsWith('sale')) return 'sales';
  if (who.startsWith('contractor')) return 'contractor';
  if (who.startsWith('execution')) return 'execution';
  return who.replace(/\s+/g, '_');
}

const partyLabel = (key) => (
  WAITING_PARTIES.find((party) => party.key === key)?.label
  || key.replace(/_/g, ' ').replace(/^\w/, (char) => char.toUpperCase())
);

/**
 * Who holds the ball: every stage item (and sub-item) whose status is "Pending from …".
 * linesByStage (the page's live, possibly edited lines) wins over stage.lines.
 * → { parties: [{ key, label, items: [{ stageId, stageName, lineId, label }] }], total,
 *     byStage: { [stageId]: [{ key, label, count }] } }
 */
export function collectWaitingOn(stages = [], linesByStage = {}) {
  const parties = WAITING_PARTIES.map((party) => ({ ...party, items: [] }));
  const byKey = new Map(parties.map((party) => [party.key, party]));
  const byStage = {};

  (stages || []).forEach((stage) => {
    const lines = linesByStage?.[stage.id] || stage.lines || [];
    const visit = (line, parent) => {
      if (!line || typeof line !== 'object') return;
      const key = waitingPartyOf(line.status);
      if (key) {
        let party = byKey.get(key);
        if (!party) {
          party = { key, label: partyLabel(key), items: [] };
          byKey.set(key, party);
          parties.push(party);
        }
        party.items.push({
          stageId: stage.id,
          stageName: text(stage.name),
          lineId: line.id,
          label: text(line.name) || text(parent?.name) || text(stage.name) || 'Item',
        });
        const stageParties = byStage[stage.id] || (byStage[stage.id] = []);
        const existing = stageParties.find((entry) => entry.key === key);
        if (existing) existing.count += 1;
        else stageParties.push({ key, label: party.label, status: text(line.status), count: 1 });
      }
      (line.sub_items || []).forEach((subItem) => visit(subItem, line));
    };
    lines.forEach((line) => visit(line, null));
  });

  return {
    parties,
    byStage,
    total: parties.reduce((sum, party) => sum + party.items.length, 0),
  };
}

const LPO_LOI_STAGE_NAME = 'lpo/loi';
export const LPO_LOI_PERMISSION = 'stage_lpo_loi';

/**
 * LPO/LOI stage visibility = the `stage_lpo_loi` permission (Admin › Access; Super Admin, Admin and
 * Presales by default). Without a permission list on the user, Admin and Presales see it by role.
 * Convenience only: the backend already leaves the stage out of the project for roles without it.
 */
export function canSeeLpoLoiStage(stage, user) {
  if (text(stage?.name).toLowerCase() !== LPO_LOI_STAGE_NAME) return true;
  if (!user) return false;
  return hasPermission(user, LPO_LOI_PERMISSION);
}

export const isAdminLike = (user) => ['Admin', 'Super Admin'].includes(text(user?.role));

/* ------------------------------------------------------------------ execution */

const DONE = 'done';
const NOT_REQUIRED = 'not required';
const statusKey = (status) => text(status).toLowerCase() || 'not started';

/** Status counts of the main (level 1) execution tasks. `percent` ignores "Not required" tasks. */
export function summarizeExecution(tasks = []) {
  const main = (tasks || []).filter((task) => Number(task?.level || 1) === 1);
  const counts = { done: 0, notRequired: 0, notStarted: 0, inProgress: 0, bottleneck: 0, waiting: 0, other: 0 };
  main.forEach((task) => {
    const key = statusKey(task.status);
    if (key === DONE) counts.done += 1;
    else if (key === NOT_REQUIRED) counts.notRequired += 1;
    else if (key === 'not started') counts.notStarted += 1;
    else if (key === 'in progress') counts.inProgress += 1;
    else if (key === 'bottleneck') counts.bottleneck += 1;
    else if (key === 'waiting for approval' || key === 'update required') counts.waiting += 1;
    else counts.other += 1;
  });
  const counted = main.length - counts.notRequired;
  return {
    total: main.length,
    counted,
    ...counts,
    open: counted - counts.done,
    percent: counted > 0 ? Math.round((counts.done / counted) * 100) : null,
    datedCount: (tasks || []).filter((task) => task?.start_date || task?.due_date).length,
  };
}

/** Progress of a parent from its direct children: { done, total, percent } — null without children. */
export function getChildProgress(children = []) {
  if (!children?.length) return null;
  const relevant = children.filter((child) => statusKey(child.status) !== NOT_REQUIRED);
  if (!relevant.length) return { done: 0, total: 0, percent: null, notRequired: true };
  const done = relevant.filter((child) => statusKey(child.status) === DONE).length;
  return { done, total: relevant.length, percent: Math.round((done / relevant.length) * 100) };
}

const collectDescendants = (tasks, taskId) => {
  const byParent = new Map();
  (tasks || []).forEach((task) => {
    if (!task?.parent_id) return;
    if (!byParent.has(task.parent_id)) byParent.set(task.parent_id, []);
    byParent.get(task.parent_id).push(task);
  });
  const result = [];
  const stack = [...(byParent.get(taskId) || [])];
  while (stack.length) {
    const task = stack.shift();
    result.push(task);
    stack.push(...(byParent.get(task.id) || []));
  }
  return result;
};

const sameValue = (a, b) => {
  if (typeof a === 'boolean' || typeof b === 'boolean') return Boolean(a) === Boolean(b);
  return text(a) === text(b);
};

/** Fields that a parent change copies to every sub-item (mirrors the page's cascade). */
export const EXECUTION_CASCADE_FIELDS = ['status', 'category', 'start_date', 'due_date', 'done_by', 'checked_by', 'comments', 'monitoring'];
/** Fields whose change is announced with an Undo toast (typing fields are not, so no toast per keystroke). */
export const EXECUTION_ANNOUNCED_FIELDS = ['status', 'category', 'start_date', 'due_date', 'done_by', 'checked_by', 'monitoring'];
/**
 * Announced fields edited in several steps (typing a date, ticking several names): on a parent only the
 * parent changes while the edit is in progress; sub-items follow — with one confirm / toast — when the
 * edit is committed (date input blur, people popover closed).
 */
export const EXECUTION_COMMIT_ON_BLUR_FIELDS = ['start_date', 'due_date', 'done_by', 'checked_by'];

/** True for a date the person is still typing / that cannot be meant: not YYYY-MM-DD, or a year before 1900. */
export function isIncompleteDate(value) {
  const raw = text(value);
  if (!raw) return false;
  const match = raw.match(/^(\d{4,})-(\d{2})-(\d{2})$/);
  if (!match) return true;
  const year = Number(match[1]);
  return year < 1900 || year > 9999;
}

/**
 * What a parent change will do to its sub-items:
 *   { descendants, changed, overwritten, overwrittenSummary }
 * `overwritten` = recorded work that would be lost: sub-items that are Done and would stop being Done,
 * or that have their own different person / date / comment in that field.
 */
export function describeExecutionCascade(tasks, taskId, field, value, { previousValue } = {}) {
  if (!EXECUTION_CASCADE_FIELDS.includes(field)) {
    return { descendants: 0, changed: 0, overwritten: 0, overwrittenSummary: '' };
  }
  const descendants = collectDescendants(tasks, taskId);
  const changed = descendants.filter((task) => !sameValue(task[field], value));
  // The parent's value before this edit started (the page passes it for edits committed on blur, when the
  // parent already shows the new value). A sub-item that simply carries that value inherited it.
  const parentBefore = previousValue !== undefined
    ? previousValue
    : (tasks || []).find((task) => task?.id === taskId)?.[field];
  let overwritten = [];
  if (field === 'status') {
    overwritten = statusKey(value) === DONE ? [] : changed.filter((task) => statusKey(task.status) === DONE);
  } else if (['done_by', 'checked_by', 'comments', 'start_date', 'due_date'].includes(field)) {
    overwritten = changed.filter((task) => text(task[field]) && !sameValue(task[field], parentBefore));
  }
  let overwrittenSummary = '';
  if (overwritten.length) {
    overwrittenSummary = field === 'status'
      ? `${overwritten.length} sub-item${overwritten.length === 1 ? ' is' : 's are'} already Done`
      : `${overwritten.length} sub-item${overwritten.length === 1 ? ' has' : 's have'} a different value recorded`;
  }
  return { descendants: descendants.length, changed: changed.length, overwritten: overwritten.length, overwrittenSummary };
}

/** Execution table columns. `always` columns cannot be hidden; `default` ones show on first visit. */
export const EXECUTION_COLUMNS = [
  { key: 'task', label: 'Task', always: true, default: true },
  { key: 'status', label: 'Status', default: true },
  { key: 'progress', label: 'Progress', default: true },
  { key: 'category', label: 'Category', default: true },
  { key: 'done_by', label: 'Done by', default: true },
  { key: 'due_date', label: 'Due', default: true },
  { key: 'zone', label: 'Zone', default: true },
  { key: 'start_date', label: 'Start' },
  { key: 'checked_by', label: 'Checked by' },
  { key: 'comments', label: 'Comments' },
  { key: 'monitoring', label: 'Monitoring' },
  { key: 'zone_2', label: 'Zone 2' },
  { key: 'zone_3', label: 'Zone 3' },
  { key: 'device_type', label: 'Device type' },
  { key: 'updated', label: 'Updated' },
];
export const DEFAULT_EXECUTION_COLUMNS = EXECUTION_COLUMNS.filter((column) => column.default).map((column) => column.key);

/** Keeps known keys, always includes the fixed ones, falls back to the defaults. */
export function normalizeExecutionColumns(keys) {
  if (!Array.isArray(keys)) return DEFAULT_EXECUTION_COLUMNS;
  const known = new Set(EXECUTION_COLUMNS.map((column) => column.key));
  const chosen = new Set(keys.filter((key) => known.has(key)));
  EXECUTION_COLUMNS.filter((column) => column.always).forEach((column) => chosen.add(column.key));
  return EXECUTION_COLUMNS.filter((column) => chosen.has(column.key)).map((column) => column.key);
}

/* ------------------------------------------------------------------ material */

/** Delivered ÷ (BOQ + VOs) for a material row → { planned, delivered, percent, tone }. */
export function getMaterialProgress(row = {}) {
  const planned = row.totalPlannedQty !== undefined
    ? num(row.totalPlannedQty)
    : num(row.boq_qty) + num(row.variationTotal);
  const delivered = num(row.deliveredTotal);
  let percent;
  if (planned > 0) percent = Math.round((delivered / planned) * 100);
  else percent = delivered > 0 ? 100 : 0;
  const shown = Math.max(0, Math.min(100, percent));
  let tone = 'navy';
  if (percent >= 100) tone = 'green';
  else if (percent < 40) tone = 'amber';
  return { planned, delivered, percent, shown, tone, over: percent > 100 };
}

const normalizePart = (value) => {
  const part = text(value).toUpperCase();
  return part && part !== '-' ? part : '';
};

/** Part number + description: the page's "main BOQ identity" (same as ProjectDetailPage getMainBoqIdentity). */
export const materialIdentity = (item = {}) => {
  const partKey = text(item?.part_number).toUpperCase();
  const descriptionKey = text(item?.description || item?.name).toUpperCase();
  return partKey
    ? `${partKey}::${descriptionKey}`
    : `NO_PART::${descriptionKey || text(item?.id).toUpperCase()}`;
};

/**
 * ONE matching rule for the material table and the "not linked to any BOQ line" check, so the callout
 * never calls a line linked that the table does not count (or the other way round).
 * - Delivery-note line → BOQ line: a line whose boq_item_id points to an existing BOQ line counts toward
 *   exactly that line. A line without a BOQ id — or whose BOQ line no longer exists (deleted, re-imported) —
 *   counts toward the first BOQ line with the same identity (part number + description), the same fallback
 *   the delivery-note editor uses. `deliveryTargetId(line)` → that BOQ id, or null (not linked).
 * - Variation item → BOQ line: the same BOQ id, or the same part number (case-insensitive; blank and '-'
 *   never match).
 */
export function createMaterialMatcher(boqItems = [], identityOf = materialIdentity) {
  const ids = new Set();
  const parts = new Set();
  const firstIdByIdentity = new Map();
  (boqItems || []).forEach((item) => {
    if (!item) return;
    if (item.id) ids.add(item.id);
    const part = normalizePart(item.part_number);
    if (part) parts.add(part);
    const identity = identityOf(item);
    if (!firstIdByIdentity.has(identity)) firstIdByIdentity.set(identity, item.id);
  });

  const deliveryTargetId = (line) => {
    if (!line) return null;
    if (line.boq_item_id && ids.has(line.boq_item_id)) return line.boq_item_id;
    return firstIdByIdentity.get(identityOf(line)) ?? null;
  };
  const variationItemMatches = (entry, item) => {
    if (!entry || !item) return false;
    if (entry.boq_item_id && entry.boq_item_id === item.id) return true;
    const part = normalizePart(entry.part_number);
    return Boolean(part) && part === normalizePart(item.part_number);
  };
  const isVariationItemLinked = (entry) => {
    if (!entry) return true;
    if (entry.boq_item_id && ids.has(entry.boq_item_id)) return true;
    const part = normalizePart(entry.part_number);
    return Boolean(part) && parts.has(part);
  };
  return { deliveryTargetId, variationItemMatches, isVariationItemLinked };
}

/**
 * VOs and delivery notes with lines the material table cannot count toward any BOQ line (see
 * createMaterialMatcher). → { variations: ['VO-001', …], deliveryNotes: [{ number, id }], any }
 */
export function findUnlinkedMaterial({ boqItems = [], variations = [], deliveryNotes = [], identityOf = materialIdentity } = {}) {
  const matcher = createMaterialMatcher(boqItems, identityOf);

  const unlinkedVariations = (variations || [])
    .filter((variation) => Array.isArray(variation.items) && variation.items.some((item) => item && !matcher.isVariationItemLinked(item)))
    .map((variation) => text(variation.vo_number) || 'Variation')
    .sort(naturalCompare);

  const dnByNumber = new Map();
  (deliveryNotes || []).forEach((note) => {
    const lines = note.line_items || [];
    if (!lines.some((line) => line && matcher.deliveryTargetId(line) === null)) return;
    const number = text(note.dn_number) || 'Delivery note';
    if (!dnByNumber.has(number)) dnByNumber.set(number, { number, id: note.id });
  });
  const unlinkedNotes = [...dnByNumber.values()].sort((a, b) => naturalCompare(a.number, b.number));

  return {
    variations: [...new Set(unlinkedVariations)],
    deliveryNotes: unlinkedNotes,
    any: unlinkedVariations.length > 0 || unlinkedNotes.length > 0,
  };
}

/** ['VO-001','VO-002','VO-003','VO-004'] → 'VO-001…VO-004'; ['A','B'] → 'A and B'. */
export function formatCodeList(codes = []) {
  const list = [...new Set((codes || []).map(text).filter(Boolean))];
  if (!list.length) return '';
  if (list.length === 1) return list[0];
  if (list.length > 3) return `${list[0]}…${list[list.length - 1]}`;
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/* ------------------------------------------------------------------ structure */

const DEVICE_CATEGORIES = [
  { key: 'push', label: 'push buttons', test: /push.?button|keypad|\bbuttons?\b|glass.?switch/ },
  { key: 'touch', label: 'touch panels', test: /touch|display|screen/ },
  { key: 'sensor', label: 'sensors', test: /sensor|detector|presence|motion|\bpir\b|thermostat|temperature|humidity|\blux\b|insight/ },
  { key: 'actuator', label: 'actuators', test: /actuator|dimm|relay|master|fan.?coil|\bfcu\b|controller|valve|blind|shutter|curtain|driver|switch|\brpc\b/ },
  { key: 'gateway', label: 'gateways', test: /gateway|router|coupler|power.?supply|\bpsu\b|interface|network|\bip\b/ },
];

export function categorizeDevice(device = {}) {
  const haystack = `${text(device.device_type)} ${text(device.name)} ${text(device.part_number)}`.toLowerCase();
  const match = DEVICE_CATEGORIES.find((category) => category.test.test(haystack));
  return match ? match.key : 'other';
}

// BMS trees store everything as nested "sub" nodes typed by device_type; panels and networks are
// containers, every other typed sub (FCU, PIR, RPC, SXW …) is a device.
const STRUCTURE_CONTAINER_NODES = new Set(['network', 'network_panel']);
const STRUCTURE_CONTAINER_TYPES = new Set(['panel', 'ip network', 'rs485 network']);
const nodeChildren = (node) => (Array.isArray(node?.children) ? node.children : (Array.isArray(node?.devices) ? node.devices : []));
const isPanelNode = (node) => text(node?.node_type).toLowerCase() === 'network_panel' || text(node?.device_type).toLowerCase() === 'panel';
const isContainerNode = (node) => (
  STRUCTURE_CONTAINER_NODES.has(text(node?.node_type).toLowerCase())
  || STRUCTURE_CONTAINER_TYPES.has(text(node?.device_type).toLowerCase())
);

const countDevices = (nodes = [], onDevice, onPanel) => {
  let count = 0;
  (nodes || []).forEach((node) => {
    if (!node || typeof node !== 'object') return;
    if (isContainerNode(node)) {
      if (onPanel && isPanelNode(node)) onPanel(node);
    } else {
      count += 1;
      if (onDevice) onDevice(node);
    }
    count += countDevices(nodeChildren(node), onDevice, onPanel);
  });
  return count;
};

const isZonePanel = (panel) => {
  const type = text(panel?.type).toLowerCase();
  return type ? type === 'zone' : text(panel?.name).toLowerCase().startsWith('zone');
};

/** Device nodes in a list of structure nodes (recursive; BMS containers like subs / networks are not devices). */
export const countDeviceNodes = (nodes = []) => countDevices(nodes);

/** Device count of one building / floor / room / panel (every nested device node, no containers). */
export function countNodeDevices(node = {}) {
  let count = countDevices(node.devices || []);
  (node.panels || []).forEach((panel) => { count += countDevices(panel.devices || []); });
  (node.rooms || []).forEach((room) => { count += countNodeDevices(room); });
  (node.floors || []).forEach((floor) => { count += countNodeDevices(floor); });
  return count;
}

/** Whole-structure totals + device mix: { buildings, floors, rooms, panels, zones, devices, mix: [{ key, label, count }] } */
export function summarizeStructure(structure = {}) {
  const totals = { buildings: 0, floors: 0, rooms: 0, panels: 0, zones: 0, devices: 0 };
  const mix = new Map();
  const onDevice = (device) => {
    const key = categorizeDevice(device);
    mix.set(key, (mix.get(key) || 0) + 1);
  };
  const countPanels = (panels = []) => {
    (panels || []).forEach((panel) => {
      if (isZonePanel(panel)) totals.zones += 1;
      else totals.panels += 1;
      totals.devices += countDevices(panel.devices || [], onDevice);
    });
  };
  const onPanel = () => { totals.panels += 1; };
  (structure?.buildings || []).forEach((building) => {
    totals.buildings += 1;
    (building.floors || []).forEach((floor) => {
      totals.floors += 1;
      totals.devices += countDevices(floor.devices || [], onDevice, onPanel);
      countPanels(floor.panels);
      (floor.rooms || []).forEach((room) => {
        totals.rooms += 1;
        totals.devices += countDevices(room.devices || [], onDevice, onPanel);
        countPanels(room.panels);
      });
    });
  });
  const labels = Object.fromEntries(DEVICE_CATEGORIES.map((category) => [category.key, category.label]));
  return {
    ...totals,
    mix: [...mix.entries()]
      .map(([key, count]) => ({ key, label: labels[key] || 'other', count }))
      .sort((a, b) => (a.key === 'other') - (b.key === 'other') || b.count - a.count),
  };
}

/* ------------------------------------------------------------------ invoicing */

/**
 * One stacked bar for the whole contract (base + approved VOs). `base` and `variations` are
 * summarizePaymentScopes() results for the base scope and for the approved VOs.
 * → { total, baseValue, variationsValue, receivedPercent, segments: [{ key, label, group, amount, percent, tone }] }
 */
export function buildMoneyBar({ baseValue = 0, variationsValue = 0, base = {}, variations = {} } = {}) {
  const segments = [
    { key: 'base-received', group: 'base', label: 'Received', amount: num(base.totalReceived), tone: 'green' },
    { key: 'base-pending', group: 'base', label: 'Pending payment', amount: num(base.totalPending), tone: 'blue' },
    { key: 'base-not-invoiced', group: 'base', label: 'Not invoiced', amount: num(base.totalNotInvoiced), tone: 'amber' },
    { key: 'vo-received', group: 'variations', label: 'VO received', amount: num(variations.totalReceived), tone: 'violet' },
    { key: 'vo-pending', group: 'variations', label: 'VO pending payment', amount: num(variations.totalPending), tone: 'violet-mid' },
    { key: 'vo-not-invoiced', group: 'variations', label: 'VO not invoiced', amount: num(variations.totalNotInvoiced), tone: 'violet-soft' },
  ];
  const total = num(baseValue) + num(variationsValue);
  const scale = Math.max(total, segments.reduce((sum, segment) => sum + Math.max(0, segment.amount), 0));
  const received = num(base.totalReceived) + num(variations.totalReceived);
  return {
    total,
    baseValue: num(baseValue),
    variationsValue: num(variationsValue),
    received,
    receivedPercent: total > 0 ? (received / total) * 100 : 0,
    segments: segments.map((segment) => ({
      ...segment,
      percent: scale > 0 ? (Math.max(0, segment.amount) / scale) * 100 : 0,
    })),
  };
}

/** Invoice counts for the scope switcher: { base, total, [variationId]: n }. */
export function countInvoicesByScope(invoices = [], variationInvoices = [], variations = []) {
  const counts = { base: (invoices || []).length };
  const approvedIds = new Set();
  (variations || []).forEach((variation) => {
    counts[variation.id] = (variationInvoices || []).filter((invoice) => invoice.variation_id === variation.id).length;
    if (variation.status === 'Approved') approvedIds.add(variation.id);
  });
  counts.total = counts.base + (variationInvoices || []).filter((invoice) => approvedIds.has(invoice.variation_id)).length;
  return counts;
}

/* ------------------------------------------------------------------ variations */

const variationValue = (variation) => num(variation?.total_value ?? variation?.financial_impact);

/** KPIs for the Variations tab. */
export function summarizeVariations(variations = [], variationInvoices = [], baseValue = 0) {
  const approved = (variations || []).filter((variation) => variation.status === 'Approved');
  const proposed = (variations || []).filter((variation) => !variation.status || variation.status === 'Proposed');
  const approvedTotal = approved.reduce((sum, variation) => sum + variationValue(variation), 0);
  const proposedTotal = proposed.reduce((sum, variation) => sum + variationValue(variation), 0);
  const approvedIds = new Set(approved.map((variation) => variation.id));
  const approvedInvoices = (variationInvoices || []).filter((invoice) => approvedIds.has(invoice.variation_id));
  const invoiced = approvedInvoices
    .filter((invoice) => (invoice.status || 'Not Started') !== 'Not Started')
    .reduce((sum, invoice) => sum + num(invoice.amount), 0);
  const received = approvedInvoices
    .filter((invoice) => invoice.status === 'Payment Received')
    .reduce((sum, invoice) => sum + num(invoice.amount), 0);
  const base = num(baseValue);
  return {
    approvedCount: approved.length,
    approvedTotal,
    proposedCount: proposed.length,
    proposedTotal,
    rejectedCount: (variations || []).filter((variation) => variation.status === 'Rejected').length,
    baseValue: base,
    contractValue: base + approvedTotal,
    upliftPercent: base > 0 ? (approvedTotal / base) * 100 : null,
    invoiced,
    received,
    invoicedPercent: approvedTotal > 0 ? (invoiced / approvedTotal) * 100 : null,
  };
}

/** Invoicing state of one VO → { invoiced, received, value, percent, state: 'none'|'partial'|'invoiced'|'received' } */
export function variationInvoicing(variation, variationInvoices = []) {
  const value = variationValue(variation);
  const invoices = (variationInvoices || []).filter((invoice) => invoice.variation_id === variation?.id);
  const invoiced = invoices.filter((invoice) => (invoice.status || 'Not Started') !== 'Not Started')
    .reduce((sum, invoice) => sum + num(invoice.amount), 0);
  const received = invoices.filter((invoice) => invoice.status === 'Payment Received')
    .reduce((sum, invoice) => sum + num(invoice.amount), 0);
  let state = 'none';
  if (invoiced > 0) state = value > 0 && received >= value ? 'received' : (value > 0 && invoiced >= value ? 'invoiced' : 'partial');
  return { value, invoiced, received, count: invoices.length, percent: value > 0 ? Math.min(100, (invoiced / value) * 100) : 0, state };
}

/** 'XX ×1 · E8331PRL1_DG ×33' + item count + total qty. */
export function variationItemsPreview(items = [], limit = 3) {
  const list = (items || []).filter((item) => item && typeof item === 'object');
  const parts = list.map((item) => {
    const code = text(item.part_number) && text(item.part_number) !== '-' ? text(item.part_number) : (text(item.description) || 'Item');
    const qty = num(item.qty);
    return `${code} ×${qty % 1 === 0 ? qty : qty.toFixed(2)}`;
  });
  const shown = parts.slice(0, limit).join(' · ');
  const onlyDescription = list.length === 1 && text(list[0].part_number) && text(list[0].description) ? text(list[0].description) : '';
  let previewText = parts.length > limit ? `${shown} · +${parts.length - limit} more` : shown;
  if (onlyDescription) previewText = `${shown} · ${onlyDescription}`;
  return {
    count: list.length,
    qty: list.reduce((sum, item) => sum + num(item.qty), 0),
    text: previewText,
  };
}

const THOUSANDS_GROUPED = /^\d{1,3}(,\d{3})+(\.\d*)?$/;

/**
 * An amount a person typed → { value, error }. Commas are read only as thousands separators
 * ('40,000' → 40000, '40,000.50' → 40000.5, 'AED 1,200' → 1200). A comma that cannot be one ('40,5',
 * '4000,50') is rejected with a message instead of being guessed, so '40,000' is never saved as 40.
 */
export function parseMoneyInput(raw) {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? { value: raw, error: '' } : { value: null, error: 'Enter a valid amount' };
  }
  const input = text(raw);
  if (!input) return { value: null, error: 'Enter an amount' };
  const digits = input.replace(/[^\d.,]/g, '');
  if (digits.includes(',') && !THOUSANDS_GROUPED.test(digits)) {
    return { value: null, error: 'Use commas only between thousands, and a dot for decimals: 40,000.50' };
  }
  const value = toNumber(input);
  return value === null ? { value: null, error: 'Enter a valid amount, e.g. 40,000.50' } : { value, error: '' };
}

/* ------------------------------------------------------------------ timeline */

const CLOSED_PROJECT_STATUSES = new Set(['completed', 'cancelled', 'canceled', 'closed']);

/**
 * Handover (project end date) state. days > 0 = in the past.
 * → null without an end date, else { endDate, days, overdue, onHold, closed }
 */
export function getHandoverState(project, { now } = {}) {
  const endDate = project?.end_date;
  if (!endDate) return null;
  const days = ageDays(endDate, { now });
  if (days === null) return null;
  const status = text(project?.status).toLowerCase();
  const closed = CLOSED_PROJECT_STATUSES.has(status);
  return {
    endDate,
    days,
    closed,
    onHold: status === 'on hold',
    overdue: days > 0 && !closed,
  };
}

/* ------------------------------------------------------------------ files */

/** 'Projects\\35. LIV Waterside\\HA' → ['Projects', '35. LIV Waterside', 'HA'] */
export const splitFolderPath = (path) => String(path || '').split(/[\\/]+/).map((part) => part.trim()).filter(Boolean);
